export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly details: Array<{ path: string; message: string }> = [],
  ) {
    super(code);
  }
}

/** JSON call to the API on this origin (/api). Cookies are first-party; nothing is sent cross-site. */
export async function api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network');
  }
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    /* empty body */
  }
  if (!res.ok) {
    const d = (data ?? {}) as {
      error?: string;
      details?: Array<{ path: string; message: string }>;
    };
    throw new ApiError(res.status, d.error ?? 'error', d.details);
  }
  return data as T;
}

export interface Site {
  id: string;
  name: string;
  domain: string;
  verified: boolean;
  consentRequired: boolean;
  antiFlickerMs: number | null;
  lastPingAt: string | null;
}

export interface Variation {
  id: string;
  name?: string;
  weight: number;
  type: 'control' | 'changeset' | 'redirect';
  /** multivariate only: which option of each section this combination shows */
  levels?: Record<string, string>;
}

export type Goal = { id: string; name?: string; revenue?: boolean } & (
  | { type: 'click'; selector: string }
  | { type: 'pageview'; rule: { type: string; value: string } }
  | { type: 'custom'; event: string }
  | { type: 'datalayer'; event: string }
);

export interface Experiment {
  id: string;
  siteId: string;
  name: string;
  status: 'draft' | 'running' | 'paused' | 'stopped';
  allocation: number;
  variations: Variation[];
  goals: Goal[];
  /** multivariate only */
  factors?: Array<{ id: string; name: string; options: Array<{ id: string; name: string }> }>;
  version: number;
}

export interface PublishState {
  version: number | null;
  unpublishedChanges: boolean;
  blocked: Array<{ path: string; message: string }> | null;
}

/** A sentence a person can act on, for whatever went wrong. */
export function explain(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!(e instanceof ApiError)) return fallback;
  switch (e.code) {
    case 'network':
      return 'Could not reach the server. Check your connection and try again.';
    case 'invalid_email':
      return 'Please enter a valid email address.';
    case 'too_many_requests':
      return 'Too many attempts. Please wait a little and try again.';
    case 'invalid_or_expired':
      return 'This link has expired or has already been used. Request a new sign-in link.';
    case 'domain_already_added':
      return 'You have already added that domain.';
    case 'limit':
      return 'You have reached the limit for your plan.';
    case 'invalid_domain':
      return 'Enter the domain only, like shop.example.com (no spaces, no IP addresses).';
    case 'invalid_name':
      return 'Enter a name between 1 and 80 characters.';
    case 'not_signed_in':
      return 'You have been signed out. Please sign in again.';
    case 'publish_blocked':
      return `Cannot publish: ${e.details[0]?.message ?? 'the configuration is not valid'}.`;
    case 'invalid':
      return e.details[0] ? `${e.details[0].path || 'Input'} ${e.details[0].message}.` : fallback;
    default:
      return fallback;
  }
}

export interface ShareStatus {
  active: boolean;
  createdAt: string | null;
  expiresAt: string | null;
}

export interface AnalysisVariation {
  id: string;
  isControl: boolean;
  visitors: number;
  converters: number;
  rate: number | null;
  probBest?: number;
  vsControl?: {
    lift: number | null;
    liftInterval: [number, number] | null;
    probBeatsControl: number | null;
    tier: string;
    reason: string;
  };
  revenue?: Record<string, unknown>;
}

export interface Analysis {
  verdict: {
    tier: string;
    winner: string | null;
    headline: string;
    progress: number;
    plannedPerVariation: number;
    days: number;
    config: Record<string, number>;
  };
  srm: { chi2: number; pValue: number; mismatch: boolean } | null;
  variations: AnalysisVariation[];
  warnings: Array<{ code: string; message: string }>;
}

/** A multivariate test's section: the same analysis, over its options pooled across combinations. */
export interface SectionAnalysis extends Analysis {
  id: string;
  originalOption: string;
}

export interface Section {
  id: string;
  name: string;
  options: Array<{ id: string; name: string }>;
}

/** What GET /experiments/:id/results (and the public /shared/:token) returns. */
export interface ResultsView {
  experiment: { name: string; status: string };
  goals: Array<{ id: string; name: string; revenue: boolean }>;
  goalId: string | null;
  startedAt: string | null;
  days: number;
  variations: Array<{
    id: string;
    name: string;
    isControl: boolean;
    visitors: number;
    levels?: Record<string, string>;
  }>;
  /** multivariate only */
  factors: Section[] | null;
  analysis:
    | (Analysis & {
        factors?: SectionAnalysis[];
        factorsOverall?: { tier: string; recommended: Record<string, string> };
      })
    | null;
  unavailable: 'no_goal' | 'no_data' | 'stats_unavailable' | null;
  daily: {
    days: string[];
    series: Record<string, { visitors: number[]; converters: number[] }>;
  };
  forecast: { visitorsPerDay: number; remainingDays: number | null };
  revenueTruncated: boolean;
  /** owners only */
  health?: Array<{ message: string }>;
}
